import { useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from "recharts";
import { Download, Sparkles, Eye, UserPlus, Rocket, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { fetchReferralFunnel, funnelToCsv, type FunnelDay } from "@/lib/referral";

type Range = 7 | 30 | 90;

export default function FunnelPanel() {
  const [days, setDays] = useState<Range>(30);
  const [rows, setRows] = useState<FunnelDay[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    fetchReferralFunnel(days).then((r) => {
      if (!active) return;
      setRows(r);
      setLoading(false);
    });
    return () => {
      active = false;
    };
  }, [days]);

  const totals = useMemo(() => {
    const t = rows.reduce(
      (acc, r) => ({
        clicks: acc.clicks + r.clicks,
        signups: acc.signups + r.signups,
        conversions: acc.conversions + r.conversions,
      }),
      { clicks: 0, signups: 0, conversions: 0 },
    );
    const csRate = t.clicks > 0 ? (t.signups / t.clicks) * 100 : 0;
    const scRate = t.signups > 0 ? (t.conversions / t.signups) * 100 : 0;
    const overall = t.clicks > 0 ? (t.conversions / t.clicks) * 100 : 0;
    return { ...t, csRate, scRate, overall };
  }, [rows]);

  const chartData = useMemo(
    () =>
      rows.map((r) => ({
        ...r,
        label: new Date(r.day).toLocaleDateString(undefined, {
          month: "short",
          day: "numeric",
        }),
      })),
    [rows],
  );

  const exportCsv = () => {
    if (rows.length === 0) {
      toast.error("Nothing to export yet.");
      return;
    }
    const csv = funnelToCsv(rows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `wekit-referral-funnel-${days}d-${new Date()
      .toISOString()
      .slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Funnel CSV downloaded");
  };

  return (
    <Card className="border-border/50 bg-card/50 backdrop-blur">
      <CardContent className="p-6 space-y-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h2 className="font-display font-semibold text-foreground flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-primary" /> Your invite journey
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              See how friends discover WeKIT™, join in, and start their journey through your link.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Tabs value={String(days)} onValueChange={(v) => setDays(Number(v) as Range)}>
              <TabsList className="h-8">
                <TabsTrigger value="7" className="text-xs px-2.5 h-6">Last 7 days</TabsTrigger>
                <TabsTrigger value="30" className="text-xs px-2.5 h-6">Last 30 days</TabsTrigger>
                <TabsTrigger value="90" className="text-xs px-2.5 h-6">Last 90 days</TabsTrigger>
              </TabsList>
            </Tabs>
            <Button
              size="sm"
              variant="outline"
              onClick={exportCsv}
              className="gap-1.5 h-8"
            >
              <Download className="h-3.5 w-3.5" /> Export
            </Button>
          </div>
        </div>

        {/* Journey KPIs */}
        <div className="grid grid-cols-3 gap-2 sm:gap-3">
          <Kpi
            icon={Eye}
            label="Discovered"
            value={totals.clicks}
            sub={`Visited your link in ${days} days`}
          />
          <Kpi
            icon={UserPlus}
            label="Joined"
            value={totals.signups}
            sub={`${totals.csRate.toFixed(1)}% of visitors signed up`}
          />
          <Kpi
            icon={Rocket}
            label="Started journey"
            value={totals.conversions}
            sub={`${totals.scRate.toFixed(1)}% of signups activated`}
            accent
          />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-muted-foreground">
          <span>
            Visit → activation rate:{" "}
            <span className="text-foreground font-semibold">
              {totals.overall.toFixed(1)}%
            </span>
          </span>
          <div className="flex items-center gap-3">
            <Link to="/refer#invites" className="inline-flex items-center gap-1 text-primary hover:underline">
              View invites <ArrowRight className="h-3 w-3" />
            </Link>
            <Link to="/refer#sources" className="inline-flex items-center gap-1 text-primary hover:underline">
              View sources <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
        </div>

        {/* Chart */}
        <div className="h-56 w-full">
          {loading ? (
            <div className="h-full w-full rounded-lg bg-muted/30 animate-pulse" />
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                <defs>
                  <linearGradient id="gClicks" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gSignups" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(var(--accent))" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="hsl(var(--accent))" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gConv" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#22c55e" stopOpacity={0.45} />
                    <stop offset="100%" stopColor="#22c55e" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                  tickLine={false}
                  axisLine={false}
                  minTickGap={20}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                  tickLine={false}
                  axisLine={false}
                  allowDecimals={false}
                  width={28}
                />
                <Tooltip
                  contentStyle={{
                    background: "hsl(var(--background))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                  labelStyle={{ color: "hsl(var(--foreground))", fontWeight: 600 }}
                />
                <Legend
                  wrapperStyle={{ fontSize: 11 }}
                  iconType="circle"
                  iconSize={8}
                />
                <Area
                  type="monotone"
                  dataKey="clicks"
                  name="Discovered"
                  stroke="hsl(var(--primary))"
                  strokeWidth={2}
                  fill="url(#gClicks)"
                />
                <Area
                  type="monotone"
                  dataKey="signups"
                  name="Joined"
                  stroke="hsl(var(--accent))"
                  strokeWidth={2}
                  fill="url(#gSignups)"
                />
                <Area
                  type="monotone"
                  dataKey="conversions"
                  name="Started journey"
                  stroke="#22c55e"
                  strokeWidth={2}
                  fill="url(#gConv)"
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>

        {!loading && totals.clicks === 0 && (
          <p className="text-xs text-muted-foreground text-center pt-2">
            No visits yet in this window. Share your link and check back — every
            click shows up here as part of someone's journey.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function Kpi({
  icon: Icon,
  label,
  value,
  sub,
  accent,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: number;
  sub: string;
  accent?: boolean;
}) {
  return (
    <div
      className={`rounded-xl border p-3 ${
        accent ? "border-primary/30 bg-primary/5" : "border-border bg-background/40"
      }`}
    >
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <p className="font-display text-2xl font-bold text-foreground mt-1 tabular-nums">
        {value.toLocaleString()}
      </p>
      <p className="text-[11px] text-muted-foreground mt-0.5">{sub}</p>
    </div>
  );
}
